/**
 * Theme-aware color definitions for light/dark mode compatibility.
 * These colors are designed to work well in both light and dark themes.
 *
 * Usage:
 * - Store the color ID (e.g., 'blue', 'green') in the database
 * - Use resolveThemeColor() to get the correct hex value for the current theme
 * - Use ThemeAwareColorTiles component for color selection UI
 */

export interface ThemeAwareColor {
	id: string;
	name: string;
	light: string;
	dark: string;
}

/**
 * Curated set of 12 colors that work well in both light and dark themes.
 * Light mode uses Tailwind 500-level colors, dark mode uses 400-level colors.
 */
export const THEME_AWARE_COLORS: Array<ThemeAwareColor> = [
	{
		id: 'default-text',
		name: 'Default Text',
		light: '#111827',
		dark: '#F9FAFB',
	},
	{ id: 'blue', name: 'Blue', light: '#3B82F6', dark: '#60A5FA' },
	{ id: 'green', name: 'Green', light: '#10B981', dark: '#34D399' },
	{ id: 'purple', name: 'Purple', light: '#8B5CF6', dark: '#A78BFA' },
	{ id: 'amber', name: 'Amber', light: '#F59E0B', dark: '#FBBF24' },
	{ id: 'red', name: 'Red', light: '#EF4444', dark: '#F87171' },
	{ id: 'pink', name: 'Pink', light: '#EC4899', dark: '#F472B6' },
	{ id: 'cyan', name: 'Cyan', light: '#06B6D4', dark: '#22D3EE' },
	{ id: 'gray', name: 'Gray', light: '#6B7280', dark: '#9CA3AF' },
	{ id: 'teal', name: 'Teal', light: '#14B8A6', dark: '#2DD4BF' },
	{ id: 'orange', name: 'Orange', light: '#F97316', dark: '#FB923C' },
	{ id: 'lime', name: 'Lime', light: '#84CC16', dark: '#A3E635' },
];

/**
 * Default color ID to use when no color is selected
 */
export const DEFAULT_THEME_COLOR_ID = 'blue';

/**
 * Get a ThemeAwareColor by its ID.
 * Returns undefined if not found.
 */
export function getThemeAwareColorById(
	colorId: string,
): ThemeAwareColor | undefined {
	return THEME_AWARE_COLORS.find((c) => c.id === colorId);
}

/**
 * Check if a value is a legacy hex color (starts with #).
 */
export function isLegacyHexColor(value: string | null | undefined): boolean {
	if (value == null) {
		return false;
	}

	return value.startsWith('#');
}

/**
 * Resolve a color value (ID or legacy hex) to the appropriate hex value for the current theme.
 *
 * @param colorValue - Color ID (e.g., 'blue') or legacy hex (e.g., '#3B82F6')
 * @param isDark - Whether dark mode is active
 * @returns The hex color value to use
 *
 * @example
 * // New color ID - adapts to theme
 * resolveThemeColor('blue', false) // '#3B82F6' (light mode)
 * resolveThemeColor('blue', true)  // '#60A5FA' (dark mode)
 *
 * // Legacy hex - returns as-is
 * resolveThemeColor('#FF0000', false) // '#FF0000'
 * resolveThemeColor('#FF0000', true)  // '#FF0000'
 */
export function resolveThemeColor(
	colorValue: string | null | undefined,
	isDark: boolean,
): string | null {
	// No value - return null to allow fallback
	if (colorValue == null || colorValue === '') {
		return null;
	}

	// Legacy hex value. Light mode returns it as-is; dark mode lifts it just
	// enough to stay legible (PD-15148 — tenant-supplied hues such as
	// lifecycle-stage colours rendered unchanged on dark surfaces and
	// measured as low as 2.2:1). The lift is contrast-GATED: a hex that is
	// already legible on the dark card passes through byte-identical.
	if (colorValue.startsWith('#')) {
		return isDark ? adaptLegacyHexForDark(colorValue) : colorValue;
	}

	// Find color by ID
	const color = getThemeAwareColorById(colorValue);

	if (color == null) {
		// Unknown ID - return null to allow fallback
		return null;
	}

	return isDark ? color.dark : color.light;
}

/** The dark card surface (`--bg-surface` dark) legacy hexes must read on. */
const DARK_SURFACE_RGB: readonly [number, number, number] = [31, 41, 55];

/** WCAG contrast floor for the adapted colour. */
const DARK_CONTRAST_TARGET = 4.5;

function parseHexColor(hex: string): [number, number, number] | null {
	const normalized = hex.replace('#', '');
	const expanded =
		normalized.length === 3
			? normalized
					.split('')
					.map((c) => c + c)
					.join('')
			: normalized;
	if (!/^[0-9a-fA-F]{6}$/.test(expanded)) {
		return null;
	}
	return [
		parseInt(expanded.slice(0, 2), 16),
		parseInt(expanded.slice(2, 4), 16),
		parseInt(expanded.slice(4, 6), 16),
	];
}

function relativeLuminance([r, g, b]: readonly [
	number,
	number,
	number,
]): number {
	const channel = (value: number): number => {
		const scaled = value / 255;
		return scaled <= 0.03928
			? scaled / 12.92
			: Math.pow((scaled + 0.055) / 1.055, 2.4);
	};
	return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

function contrastRatio(
	a: readonly [number, number, number],
	b: readonly [number, number, number],
): number {
	const la = relativeLuminance(a);
	const lb = relativeLuminance(b);
	return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function toHex([r, g, b]: readonly [number, number, number]): string {
	const part = (value: number): string =>
		Math.round(value).toString(16).padStart(2, '0').toUpperCase();
	return `#${part(r)}${part(g)}${part(b)}`;
}

/**
 * PD-15148 — lift a legacy (tenant-supplied) hex just far enough to read on
 * the dark card surface, by mixing toward white in 5% steps until it clears
 * 4.5:1. Contrast-gated: a hex that already reads fine (bright yellows,
 * light greys) is returned byte-identical, so only the illegible values
 * move. The hue is preserved; only luminance lifts — the same idea as the
 * vivid-outline family's lifted dark pairs.
 */
export function adaptLegacyHexForDark(hex: string): string {
	const rgb = parseHexColor(hex);
	if (rgb == null) {
		return hex;
	}

	if (contrastRatio(rgb, DARK_SURFACE_RGB) >= DARK_CONTRAST_TARGET) {
		return hex;
	}

	for (let step = 1; step <= 20; step++) {
		const share = step * 0.05;
		const mixed: [number, number, number] = [
			rgb[0] + (255 - rgb[0]) * share,
			rgb[1] + (255 - rgb[1]) * share,
			rgb[2] + (255 - rgb[2]) * share,
		];
		if (contrastRatio(mixed, DARK_SURFACE_RGB) >= DARK_CONTRAST_TARGET) {
			return toHex(mixed);
		}
	}

	return '#FFFFFF';
}

/**
 * Get the first unused color ID from a list of used colors.
 * Useful for auto-assigning colors to new items.
 */
export function getNextAvailableColorId(
	usedColorIds: Array<string | null | undefined>,
): string {
	const usedSet = new Set(usedColorIds.filter((id) => id != null));

	for (const color of THEME_AWARE_COLORS) {
		if (!usedSet.has(color.id)) {
			return color.id;
		}
	}

	// All colors used, return the first one
	return THEME_AWARE_COLORS[0]?.id ?? DEFAULT_THEME_COLOR_ID;
}
